
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Story } from '../types';
import Button from './Button';
import { generateAudioForText } from '../services/geminiService';

interface StoryViewerProps {
  story: Story;
  onReset: () => void;
  onSave?: (story: Story) => void;
  onUpdate?: (updatedStory: Story) => void;
  isSaved?: boolean;
  readOnly?: boolean; 
  canEdit?: boolean;
  t?: any;
}

/**
 * Convertit une chaîne Base64 en URL de Blob pour le lecteur audio
 */
function base64ToBlobUrl(base64: string, mimeType: string = 'audio/wav'): string {
  if (!base64) return "";
  try {
    const cleaned = base64.replace(/\s/g, '');
    const byteCharacters = atob(cleaned);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    return URL.createObjectURL(blob);
  } catch (e) {
    console.error("Base64 decoding failed", e);
    return "";
  }
}

const StoryViewer: React.FC<StoryViewerProps> = ({ story, onReset, onSave, onUpdate, isSaved, readOnly = false, canEdit = false, t }) => {
  const [currentSegmentIdx, setCurrentSegmentIdx] = useState(0);
  const [isReading, setIsReading] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0); 
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const currentSegment = story.segments ? story.segments[currentSegmentIdx] : null;

  useEffect(() => {
    if (currentSegment) {
      setEditText(currentSegment.text || '');
      setIsEditing(false);
      stopAudio();
      
      // Nettoyage de l'ancienne URL pour éviter les fuites mémoire
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      
      if (currentSegment.audioData) {
        const url = base64ToBlobUrl(currentSegment.audioData);
        if (url) setAudioUrl(url);
      }
    }
    return () => {
      stopAudio();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [currentSegmentIdx, story.id]);

  const words = useMemo(() => {
    return (currentSegment?.text || "").split(/\s+/).filter(w => w.length > 0);
  }, [currentSegment?.text]);

  const activeWordIndex = useMemo(() => {
    if (!isReading || readingProgress <= 0 || words.length === 0) return -1;
    const index = Math.floor(readingProgress * words.length);
    return Math.min(index, words.length - 1);
  }, [readingProgress, isReading, words]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsReading(false);
    setReadingProgress(0);
  };

  const handleTogglePlay = async () => {
    if (!currentSegment || isEditing || isGeneratingAudio) return;
    
    if (isReading) { 
      stopAudio(); 
      return; 
    }

    // Si pas d'audio, on le génère à la volée
    if (!audioUrl && !currentSegment.audioData) {
      await handleRegenerateAudio();
      return;
    }

    if (audioRef.current && audioUrl) {
      audioRef.current.play().catch(err => {
        console.error("Playback error", err);
        // Fallback si l'URL du blob a expiré ou est invalide
        handleRegenerateAudio();
      });
      setIsReading(true);
    }
  };

  const handleRegenerateAudio = async () => {
    if (isGeneratingAudio || !currentSegment) return;
    setIsGeneratingAudio(true);
    try {
      // Appel à votre API Cloud Run
      const audioBase64 = await generateAudioForText(currentSegment.text);
      
      if (!audioBase64) throw new Error("Aucune donnée audio reçue.");

      const updatedSegments = [...story.segments];
      updatedSegments[currentSegmentIdx] = { ...updatedSegments[currentSegmentIdx], audioData: audioBase64 };
      
      if (onUpdate) onUpdate({ ...story, segments: updatedSegments });
      
      const url = base64ToBlobUrl(audioBase64);
      if (url) {
        setAudioUrl(url);
        // Auto-play dès que possible
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play().catch(console.error);
            setIsReading(true);
          }
        }, 150);
      } else {
        throw new Error("Échec de la création de l'objet audio.");
      }
    } catch (err: any) {
      console.error("Audio Generation failed:", err);
      // Utilisation du message générique pour les erreurs de service IA
      alert(t.quotaError);
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleDownloadAudio = () => {
    if (!audioUrl) return;
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = `${story.title.replace(/\s+/g, '_')}_page_${currentSegmentIdx + 1}.wav`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onTimeUpdate = () => {
    if (audioRef.current && audioRef.current.duration) {
      const progress = audioRef.current.currentTime / audioRef.current.duration;
      setReadingProgress(progress || 0);
    }
  };

  const onAudioEnded = () => {
    setIsReading(false);
    setReadingProgress(0);
  };

  const saveEditedText = () => {
    if (!onUpdate) return;
    const updatedSegments = [...story.segments];
    updatedSegments[currentSegmentIdx] = { 
      ...updatedSegments[currentSegmentIdx], 
      text: editText,
      audioData: undefined // Forcer la régénération après édition
    };
    onUpdate({ ...story, segments: updatedSegments });
    setIsEditing(false);
    setAudioUrl(null);
    stopAudio();
  };

  const handleShare = async () => {
    const shareData = {
      title: `ⵜⵉⵏⴼⵓⵙⵉⵏ ⵏ ⵉⵎⵥⵥⵢⴰⵏⵏ: ${story.title}`,
      text: `Découvrez ce conte magique en Amazigh ! ⵣ\n\n"${story.title}"`,
      url: window.location.origin,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (err) {}
    } else {
      setShowShareMenu(!showShareMenu);
    }
  };

  if (!currentSegment) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-8 animate-story">
      {/* Lecteur invisible */}
      <audio 
        key={audioUrl} // Forcer le rechargement si l'URL change
        ref={audioRef} 
        src={audioUrl || ''} 
        onTimeUpdate={onTimeUpdate} 
        onEnded={onAudioEnded}
      />

      {/* Rendu Impression PDF */}
      <div className="print-layout">
        <div className="print-page">
          <h1 className="print-title font-tifinagh">{story.title}</h1>
          {story.segments[0]?.imageUrl && (
            <img src={story.segments[0].imageUrl} className="print-image" alt="Couverture" />
          )}
          <p className="text-indigo-400 font-bold uppercase tracking-widest mt-10">ⵣ ⵜⵉⵏⴼⵓⵙⵉⵏ ⵏ ⵉⵎⵥⵥⵢⴰⵏⵏ</p>
        </div>
        {story.segments.map((seg, idx) => (
          <div key={idx} className="print-page">
            {seg.imageUrl && <img src={seg.imageUrl} className="print-image" alt={`Page ${idx + 1}`} />}
            <p className="print-text font-tifinagh">{seg.text}</p>
          </div>
        ))}
      </div>

      {/* Contrôles du haut */}
      <div className="flex justify-between items-center no-print">
        <button onClick={onReset} className="flex items-center gap-2 text-indigo-900/60 hover:text-indigo-600 font-bold transition-colors">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m15 18-6-6 6-6"/></svg>
          RETOUR
        </button>

        <div className="flex gap-2 sm:gap-4 relative">
           {canEdit && !isEditing && (
             <Button variant="secondary" onClick={() => setIsEditing(true)} className="!px-4 sm:!px-6 !py-3 !text-[10px] sm:!text-sm">
               Modifier
             </Button>
           )}
           {onSave && !isSaved && !readOnly && (
             <Button variant="accent" onClick={() => onSave(story)} className="!px-4 sm:!px-6 !py-3 !text-[10px] sm:!text-sm">Sauvegarder</Button>
           )}
           
           <div className="relative">
            <Button variant="glass" onClick={handleShare} className="!px-3 sm:!px-4 !py-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
            </Button>
            
            {showShareMenu && (
              <div className="absolute top-full mt-2 right-0 w-48 bg-white rounded-2xl shadow-2xl border border-indigo-50 py-2 z-[100] animate-fadeIn">
                <button onClick={() => { window.open(`https://wa.me/?text=${encodeURIComponent(story.title + ' ' + window.location.origin)}`, '_blank'); setShowShareMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-indigo-900 hover:bg-indigo-50">
                  <span className="text-xl">💬</span> WhatsApp
                </button>
                <button onClick={() => { navigator.clipboard.writeText(window.location.origin); alert(t.copy); setShowShareMenu(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-indigo-900 hover:bg-indigo-50 border-t border-indigo-50">
                  <span className="text-xl">🔗</span> {t.copy}
                </button>
              </div>
            )}
           </div>

           <Button variant="glass" onClick={() => window.print()} className="!px-4 sm:!px-6 !py-3 !text-[10px] sm:!text-xs flex gap-2">
             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
             <span className="hidden sm:inline">PDF</span>
           </Button>
        </div>
      </div>

      {/* Grille principale */}
      <div className="grid lg:grid-cols-5 gap-8 items-stretch no-print">
        {/* Colonne Image */}
        <div className="lg:col-span-3 relative">
          <div className="relative w-full aspect-video md:aspect-square lg:aspect-auto lg:h-[650px] rounded-[3rem] overflow-hidden shadow-2xl border-4 border-white bg-slate-100 group">
            {currentSegment.imageUrl ? (
              <img src={currentSegment.imageUrl} alt="Conte" className="w-full h-full object-cover animate-fadeIn" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"/>
              </div>
            )}
            
            {/* Overlay Bouton Play */}
            {!isEditing && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-black/5 group-hover:bg-black/10 transition-all cursor-pointer" 
                onClick={handleTogglePlay}
              >
                 <button 
                  disabled={isGeneratingAudio}
                  className={`w-24 h-24 rounded-full flex items-center justify-center shadow-2xl transition-all active:scale-90 ${isReading ? 'bg-amber-500 text-white animate-pulse' : 'bg-white text-indigo-600 hover:scale-110'} disabled:opacity-50`}
                >
                  {isGeneratingAudio ? (
                     <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
                  ) : isReading ? (
                    <svg width="40" height="40" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                  ) : (
                    <svg width="40" height="40" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Colonne Texte & Audio */}
        <div className="lg:col-span-2 flex flex-col space-y-6">
          <div className="glass-parchment p-8 rounded-[2.5rem] flex-1 flex flex-col relative overflow-hidden min-h-[400px]">
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {isEditing ? (
                <div className="h-full flex flex-col gap-4 animate-fadeIn">
                   <textarea 
                     value={editText}
                     onChange={(e) => setEditText(e.target.value)}
                     className="flex-1 w-full bg-indigo-50/50 p-6 rounded-3xl font-tifinagh text-2xl leading-relaxed outline-none border-2 border-indigo-100 focus:border-indigo-600 transition-all resize-none"
                   />
                   <div className="flex gap-2">
                     <Button variant="accent" onClick={saveEditedText} className="flex-1">Valider</Button>
                     <Button variant="secondary" onClick={() => setIsEditing(false)} className="flex-1">Annuler</Button>
                   </div>
                </div>
              ) : (
                <div className="text-center font-tifinagh text-3xl md:text-4xl leading-relaxed text-indigo-950 flex flex-wrap justify-center gap-x-4 gap-y-6">
                  {words.map((word, idx) => (
                    <span 
                      key={idx} 
                      className={`transition-all duration-300 rounded-xl px-2 py-1 relative ${idx === activeWordIndex ? 'text-amber-900 bg-amber-200 scale-110 shadow-lg z-10 ring-2 ring-amber-100' : 'text-indigo-950'}`}
                    >
                      {word}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Section Audio Contrôles */}
            {!isEditing && (
              <div className="mt-8 flex flex-col gap-4">
                {/* Indicateur de progression si lecture en cours */}
                {isReading && (
                  <div className="w-full h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all duration-100" style={{ width: `${readingProgress * 100}%` }} />
                  </div>
                )}

                {/* Bouton de téléchargement */}
                <div className="flex flex-col gap-2">
                  {!audioUrl && !isGeneratingAudio ? (
                    <Button variant="secondary" onClick={handleRegenerateAudio} className="w-full !py-3 !text-[11px] flex gap-2">
                      <span>✨</span> ENCHANTER LA VOIX
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={handleTogglePlay} className="flex-1 !py-3 !text-[11px]">
                         {isReading ? "PAUSE" : "ÉCOUTER"}
                      </Button>
                      <Button variant="glass" onClick={handleDownloadAudio} className="flex-1 !py-3 !text-[11px] flex gap-2" disabled={!audioUrl}>
                        <span>⬇️</span> AUDIO
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Pagination Bas */}
          <div className="flex items-center justify-between px-4">
             <button 
                onClick={() => setCurrentSegmentIdx(i => Math.max(0, i-1))} 
                disabled={currentSegmentIdx === 0 || isEditing} 
                className="w-12 h-12 rounded-xl flex items-center justify-center bg-white text-indigo-900 disabled:opacity-20 shadow-sm border border-indigo-50"
             >
               <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m15 18-6-6 6-6"/></svg>
             </button>
             
             <div className="flex gap-2">
               {story.segments.map((_, idx) => (
                 <div key={idx} className={`h-1.5 rounded-full transition-all duration-500 ${idx === currentSegmentIdx ? 'w-8 bg-indigo-600' : 'w-2 bg-indigo-100'}`} />
               ))}
             </div>
             
             <button 
                onClick={() => setCurrentSegmentIdx(i => Math.min(story.segments.length - 1, i + 1))} 
                disabled={currentSegmentIdx === story.segments.length - 1 || isEditing} 
                className="w-12 h-12 rounded-xl flex items-center justify-center bg-indigo-600 text-white disabled:opacity-20 shadow-lg"
             >
               <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="3"><path d="m9 18 6-6-6-6"/></svg>
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryViewer;
